import { computed, ref, type Ref, unref, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { kvkApi } from '@services/overig/service';
import { parsePostcode } from '@tools/parsers';
import { isZakelijkeRelatie, type Relatie } from '@relatie-lib/relatieGegevens/types';
import { type CrmRelationsParticuliereRelationDto } from '@services/crm-public/orval-api/model/crmRelationsParticuliereRelationDto';
import { type CrmRelationsZakelijkeRelationDto } from '@services/crm-public/orval-api/model/crmRelationsZakelijkeRelationDto';
import { type SUREbusinessCommonDomainRechtsvormEnum } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainRechtsvormEnum';
import { isZakelijkeRelatieDto } from './relatiegegevensUtils';

export function useKvkBedrijfsProfiel(
  formValues: Relatie,
  relatie?: Ref<
    CrmRelationsZakelijkeRelationDto | CrmRelationsParticuliereRelationDto | null | undefined
  >,
) {
  const kvknummerHasChanged = ref<boolean>(false);

  watch(formValues, () => {
    if (kvknummerHasChanged.value) {
      return;
    }

    if (!relatie?.value) {
      kvknummerHasChanged.value = isZakelijkeRelatie(formValues) && !!formValues.kvknummer;
      return;
    }

    kvknummerHasChanged.value =
      !!relatie.value &&
      isZakelijkeRelatieDto(relatie.value) &&
      !!formValues &&
      isZakelijkeRelatie(formValues) &&
      !!formValues.kvknummer &&
      relatie.value.kvknummer !== formValues.kvknummer;
  });

  const kvknummer = computed(() =>
    formValues && isZakelijkeRelatie(formValues) && formValues.kvknummer
      ? formValues.kvknummer
      : '',
  );

  const { data } = useQuery({
    queryKey: ['kvk', 'getBedrijfsProfiel', kvknummer.value],
    queryFn: () => kvkApi.kvkGetBedrijfsProfiel(unref(kvknummer)),
    enabled: kvknummerHasChanged,
    select: (query) => {
      const { adres, ...rest } = query.data.result;
      return {
        ...rest,
        adres: {
          ...adres,
          huisnummertoevoeging: adres?.huisnummerToevoeging,
          postcode: parsePostcode(adres?.postcode ?? ''),
        },
        sbicode: rest.sbi?.code.replaceAll('.', ''),
        rechtsvorm: rest.rechtsvorm.code as SUREbusinessCommonDomainRechtsvormEnum,
      };
    },
  });

  return {
    bedrijfsProfiel: data,
  };
}
